import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import InputLabel from "@mui/material/InputLabel";
import {
  RecaptchaVerifier,
  signInWithPhoneNumber,
  linkWithPhoneNumber,
  PhoneAuthProvider,
  ConfirmationResult,
} from "firebase/auth";
import { getlostFirebaseAuth } from "../../utils/auth/firebaseConfig";
import { validatePhoneForCountry, formatPhoneNumber, COUNTRY_PHONE_METADATA } from "../../utils/phone";
import { decidePhoneVerifyNext } from "./phoneVerifyRouting";

// Extend Window interface to include recaptchaVerifier
declare global {
  interface Window {
    recaptchaVerifier?: RecaptchaVerifier;
  }
}

// Country codes mapping
const COUNTRY_CODES: Record<string, { code: string; name: string; flag: string }> = {
  AE: { code: "+971", name: "United Arab Emirates", flag: "🇦🇪" },
  AU: { code: "+61", name: "Australia", flag: "🇦🇺" },
  BR: { code: "+55", name: "Brazil", flag: "🇧🇷" },
  CA: { code: "+1", name: "Canada", flag: "🇨🇦" },
  CN: { code: "+86", name: "China", flag: "🇨🇳" },
  DE: { code: "+49", name: "Germany", flag: "🇩🇪" },
  ES: { code: "+34", name: "Spain", flag: "🇪🇸" },
  FR: { code: "+33", name: "France", flag: "🇫🇷" },
  GB: { code: "+44", name: "United Kingdom", flag: "🇬🇧" },
  HK: { code: "+852", name: "Hong Kong", flag: "🇭🇰" },
  IN: { code: "+91", name: "India", flag: "🇮🇳" },
  IT: { code: "+39", name: "Italy", flag: "🇮🇹" },
  JP: { code: "+81", name: "Japan", flag: "🇯🇵" },
  MX: { code: "+52", name: "Mexico", flag: "🇲🇽" },
  NZ: { code: "+64", name: "New Zealand", flag: "🇳🇿" },
  SG: { code: "+65", name: "Singapore", flag: "🇸🇬" },
  US: { code: "+1", name: "United States", flag: "🇺🇸" },
};

// Get sorted country entries by name
const getSortedCountries = () => {
  return Object.entries(COUNTRY_CODES).sort((a, b) => 
    a[1].name.localeCompare(b[1].name)
  );
};

interface PhoneAuthProps {
  onSuccess: () => void;
  onError: (error: Error) => void;
  onNeedsOnboarding?: () => void;
  isLinking?: boolean; // Set to true when adding phone to existing email account
}

const getDefaultCountryCode = (): string => {
  const locale = navigator.language || "en-US";
  const country = locale.split("-")[1]?.toUpperCase() || "US";
  return COUNTRY_CODES[country]?.code || "+1";
};

export const PhoneAuth: React.FC<PhoneAuthProps> = ({
  onSuccess,
  onError,
  onNeedsOnboarding,
  isLinking = false,
}) => {
  const [selectedCountry, setSelectedCountry] = useState(
    Object.entries(COUNTRY_CODES).find(([_, { code }]) => code === getDefaultCountryCode())?.[0] || "US"
  );
  
  // Reset phone number when country changes to avoid format mismatch
  const handleCountryChange = (newCountry: string) => {
    setSelectedCountry(newCountry);
    // Reformat existing phone number with new country's pattern
    if (phoneNumber) {
      const digitsOnly = phoneNumber.replace(/\D/g, "");
      const formatted = formatPhoneNumber(digitsOnly, newCountry);
      setPhoneNumber(formatted);
    }
  };
  const [phoneNumber, setPhoneNumber] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [confirmationResult, setConfirmationResult] = useState<ConfirmationResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recaptchaVerifier, setRecaptchaVerifier] = useState<RecaptchaVerifier | null>(null);
  const [codeSent, setCodeSent] = useState(false);

  const countryCode = COUNTRY_CODES[selectedCountry].code;

  // Initialize reCAPTCHA on mount
  useEffect(() => {
    // Always create a fresh verifier for this component instance
    const verifier = new RecaptchaVerifier(getlostFirebaseAuth.auth, "recaptcha-container", {
      size: "invisible",
      callback: () => {
        console.log("reCAPTCHA verified");
      },
    });
    setRecaptchaVerifier(verifier);

    return () => {
      // Clean up the verifier when component unmounts
      try {
        verifier.clear();
      } catch (e) {
        // Ignore cleanup errors
      }
      // Clear global reference if it exists
      if (window.recaptchaVerifier) {
        try {
          window.recaptchaVerifier.clear();
        } catch (e) {
          // Ignore cleanup errors
        }
        delete window.recaptchaVerifier;
      }
    };
  }, []);

  const validatePhoneNumber = (phone: string): { valid: boolean; error?: string } => {
    // ponytail: dev bypass — Firebase test numbers bypass SMS in dev, no real SMS sent
    if (import.meta.env.DEV) return { valid: true };

    const validation = validatePhoneForCountry(phone, selectedCountry);

    if (validation.valid) {
      return { valid: true };
    }

    // Generate user-friendly error message
    if (validation.error) {
      const { country, mask, example } = validation.error;
      const errorMessage = `Please enter a valid ${country} phone number. Format: ${mask} (e.g., ${example})`;
      return { valid: false, error: errorMessage };
    }

    return { valid: false, error: "Invalid phone number" };
  };

  const handleSendCode = async () => {
    const digitsOnly = phoneNumber.replace(/\D/g, "");
    const fullPhoneNumber = `${countryCode}${digitsOnly}`;

    const validation = validatePhoneNumber(phoneNumber);
    if (!validation.valid) {
      setError(validation.error || "Invalid phone number");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (!recaptchaVerifier) {
        throw new Error("reCAPTCHA not initialized");
      }

      let result: ConfirmationResult;
      
      if (isLinking) {
        // Link phone to existing user
        const currentUser = getlostFirebaseAuth.auth?.currentUser;
        if (!currentUser) {
          throw new Error("No authenticated user to link phone to");
        }
        console.log("Linking phone number to existing user:", currentUser.uid);
        result = await linkWithPhoneNumber(currentUser, fullPhoneNumber, recaptchaVerifier);
      } else {
        // New sign-in with phone
        result = await signInWithPhoneNumber(getlostFirebaseAuth.auth, fullPhoneNumber, recaptchaVerifier);
      }
      
      setConfirmationResult(result);
      setCodeSent(true);
      // Store phone for later use
      localStorage.setItem("userPhone", fullPhoneNumber);
    } catch (err: any) {
      console.error("Error sending SMS code:", err);
      setError(err?.message || "Failed to send verification code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setError("Verification code must be 6 digits");
      return;
    }

    if (!confirmationResult) {
      setError("Verification code not sent yet");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await confirmationResult.confirm(verificationCode);
      const user = result.user;

      if (isLinking) {
        // Already authenticated; just link the phone
        // Phone is already linked via confirmationResult
        onSuccess();
      } else {
        // New sign-in with phone
        // Phone verified, check if email exists
        localStorage.setItem("userPhone", user.phoneNumber || "");

        // Check if user has email
        const hasEmail = Boolean(user.email);

        if (!hasEmail) {
          // No email; phone-only sign-in requires email too.
          // Route through the email collection form (which will then
          // call establishAdminSession and bounce to /onboarding on a
          // 404 from the backend).
          onNeedsOnboarding?.();
          return;
        }

        // Both phone and email present; try to establish admin session
        // via the shared auth helper. Use the routing helper (instead of
        // substring-matching the error message) so a backend 404 lands
        // the user on /onboarding rather than getting bounced to /login
        // by the protected route's <Authenticated> check.
        let sessionErr: unknown = null;
        try {
          // Pass the verified phone number so the backend's auth handler
          // can include it in the users row it creates on first sign-in.
          await getlostFirebaseAuth.establishAdminSession(user.phoneNumber || undefined);
        } catch (e) {
          sessionErr = e;
        }
        const decision = decidePhoneVerifyNext({ hasEmail: true, sessionError: sessionErr });
        if (decision.route === "needsOnboarding") {
          onNeedsOnboarding?.();
        } else {
          onSuccess();
        }
      }
    } catch (err: any) {
      console.error("Error verifying code:", err);
      setError(err?.message || "Invalid verification code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Strip all non-digit characters
    const digitsOnly = e.target.value.replace(/\D/g, "");
    
    // Apply country-specific formatting
    const formatted = formatPhoneNumber(digitsOnly, selectedCountry);
    setPhoneNumber(formatted);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {error && <Alert severity="error">{error}</Alert>}

      {!codeSent ? (
        <>
          <Typography variant="body2" color="textSecondary">
            {isLinking ? "Add a phone number to your account" : "Sign in with your phone number"}
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Country</InputLabel>
                <Select
                  value={selectedCountry}
                  onChange={(e) => handleCountryChange(e.target.value)}
                  label="Country"
                  disabled={loading}
                  renderValue={(value) => (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <span style={{ fontSize: "1.2em" }}>{COUNTRY_CODES[value].flag}</span>
                      <span>{COUNTRY_CODES[value].code}</span>
                    </Box>
                  )}
                >
                  {getSortedCountries().map(([code, { name, flag, code: countryCode }]) => (
                    <MenuItem key={code} value={code}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <span style={{ fontSize: "1.2em" }}>{flag}</span>
                        <span>{countryCode}</span>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Phone Number"
                placeholder={COUNTRY_PHONE_METADATA[selectedCountry]?.placeholder || "Phone number"}
                value={phoneNumber}
                onChange={handlePhoneInputChange}
                disabled={loading}
                fullWidth
                size="small"
                inputProps={{
                  maxLength: COUNTRY_PHONE_METADATA[selectedCountry]?.placeholder.length || 20
                }}
              />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ px: 1.75 }}>
              Format: {COUNTRY_PHONE_METADATA[selectedCountry]?.placeholder || "Enter phone number"}
            </Typography>
          </Box>

          <Button
            onClick={handleSendCode}
            disabled={loading || !phoneNumber}
            fullWidth
            variant="contained"
          >
            {loading ? <CircularProgress size={20} /> : "Send Verification Code"}
          </Button>
        </>
      ) : (
        <>
          <Typography variant="body2" color="textSecondary">
            Enter the 6-digit code sent to {countryCode}
            {phoneNumber}
          </Typography>

          <TextField
            label="Verification Code"
            placeholder="000000"
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            disabled={loading}
            fullWidth
            inputProps={{ maxLength: 6 }}
            size="small"
          />

          <Button
            onClick={handleVerifyCode}
            disabled={loading || verificationCode.length !== 6}
            fullWidth
            variant="contained"
          >
            {loading ? <CircularProgress size={20} /> : "Verify Code"}
          </Button>

          <Button
            onClick={() => {
              setCodeSent(false);
              setVerificationCode("");
              setError(null);
            }}
            disabled={loading}
            fullWidth
            variant="text"
          >
            Back to Phone Number
          </Button>
        </>
      )}

      <div id="recaptcha-container" style={{ display: "none" }} />
    </Box>
  );
};
